import React, {useEffect, useState, useMemo, useCallback} from "react"
import {Navigate} from "react-router-dom";
import Appbar from "./Appbar";
import MenuList from "./MenuList";
import {
    Box, CircularProgress, Dialog, Grid, styled, Table, TableBody, TableCell, tableCellClasses,
    TableContainer, TableHead, TableRow, TextField, TableSortLabel, Paper, Typography, IconButton,
    Menu, MenuItem, Tooltip
} from "@mui/material";
import axios from "axios";
import ErrorMsg from "./msg/ErrorMsg";
import SuccsMsg from "./msg/SuccsMsg";
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import InventoryIcon from '@mui/icons-material/Inventory';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CalculateIcon from '@mui/icons-material/Calculate';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import SyncAltIcon from '@mui/icons-material/SyncAlt';

// استایل برای باکس‌های آمار
const StatsBox = styled(Paper)(({ color }) => ({
    padding: "16px",
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderRadius: 10,
    backgroundColor: color,
    color: '#fff',
    height: 100,
    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
    transition: 'transform 0.2s',
    '&:hover': {
        transform: 'translateY(-5px)',
        boxShadow: '0 6px 25px rgba(0,0,0,0.15)',
    }
}));

const StatsIcon = styled(Box)({
    fontSize: 40,
    opacity: 0.8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
});

const StatsContent = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    position: 'relative',
});

const StatsNumber = styled(Typography)({
    fontSize: 24,
    fontWeight: 'bold',
    lineHeight: 1.2,
    display: 'flex',
    alignItems: 'center',
    gap: "8px",
});

const StatsLabel = styled(Typography)({
    fontSize: 14,
    opacity: 0.9,
});

const ToggleButton = styled(IconButton)({
    color: '#fff',
    padding: 2,
    marginLeft: 4,
    '&:hover': {
        backgroundColor: 'rgba(255,255,255,0.2)',
    }
});

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: theme.palette.common.black,
        color: theme.palette.common.white,
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
    '&:last-child td, &:last-child th': {
        border: 0,
    },
}));

// کامپوننت باکس آمار با قابلیت تغییر گزینه‌ها
const ToggleStatsBox = ({ stats, statsLoading, color, icon: Icon, label, value, options, onOptionChange }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [currentLabel, setCurrentLabel] = useState(label);
    const [currentValue, setCurrentValue] = useState(value);
    const [currentOption, setCurrentOption] = useState(options[0]);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleOptionSelect = (option) => {
        setCurrentOption(option);
        setCurrentLabel(option.label);
        onOptionChange(option.key);
        handleClose();
    };

    // به‌روزرسانی مقدار هنگام تغییر stats
    useEffect(() => {
        if (stats && currentOption) {
            setCurrentValue(stats[currentOption.key]);
        }
    }, [stats, currentOption]);

    return (
        <StatsBox color={color}>
            <StatsIcon>
                <Icon sx={{ fontSize: 40 }} />
            </StatsIcon>
            <StatsContent>
                <StatsNumber variant="h5">
                    {statsLoading ? <CircularProgress size={20} color="inherit" /> : currentValue?.toLocaleString()}
                    <Tooltip title="تغییر نوع آمار">
                        <ToggleButton size="small" onClick={handleClick}>
                            <SyncAltIcon fontSize="small" />
                        </ToggleButton>
                    </Tooltip>
                </StatsNumber>
                <StatsLabel variant="body2">{currentLabel}</StatsLabel>
            </StatsContent>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
            >
                {options.map((option) => (
                    <MenuItem
                        key={option.key}
                        onClick={() => handleOptionSelect(option)}
                        selected={currentOption.key === option.key}
                    >
                        {option.label}
                    </MenuItem>
                ))}
            </Menu>
        </StatsBox>
    );
};

const Dashboard = () => {
    const [loading , setLoading] = useState(true);
    const [statsLoading, setStatsLoading] = useState(true);
    const [productList , setProductList] = useState([]);
    const [stats, setStats] = useState({
        today_display: '',
        today: '',
        total_products: 0,
        total_stock: 0,
        today_stock: 0,
        stock_in_way: 0,
        today_stock_after_sales: 0,
        total_stock_with_in_way: 0,
        today_sales: 0,
        total_sales: 0
    });
    const [errorMsg, setErrorMsg] = useState("");
    const [succsMsg, setSuccsMsg] = useState("");
    const [search , setSearch] = useState("");

    // State for sorting
    const [sortConfig, setSortConfig] = useState({
        field: 'name',
        direction: 'asc'
    });

    // گزینه‌های قابل تغییر برای باکس موجودی
    const stockOptions = [
        { key: 'today_stock', label: 'موجودی امروز (کیلوگرم)' },
        { key: 'today_stock_after_sales', label: 'موجودی پس از فروش امروز (کیلوگرم)' },
        { key: 'stock_in_way', label: 'موجودی در راه (کیلوگرم)' },
        { key: 'total_stock_with_in_way', label: 'مجموع موجودی + در راه (کیلوگرم)' },
    ];

    // Memoized sorted data - برای افزایش سرعت لود
    const sortedProductList = useMemo(() => {
        if (!productList.length) return [];

        return [...productList].sort((a, b) => {
            const { field, direction } = sortConfig;

            if (field === 'name') {
                if (direction === 'asc') {
                    return (a.name || '').localeCompare(b.name || '', 'fa');
                } else {
                    return (b.name || '').localeCompare(a.name || '', 'fa');
                }
            } else {
                const valueA = Number(a[field]) || 0;
                const valueB = Number(b[field]) || 0;

                if (direction === 'asc') {
                    return valueA - valueB;
                } else {
                    return valueB - valueA;
                }
            }
        });
    }, [productList, sortConfig]);

    // دریافت آمار داشبورد
    useEffect(() => {
        const fetchStats = async () => {
            setStatsLoading(true);
            try {
                const data = {
                    token: localStorage.getItem("adminlogintoken")
                };
                const response = await axios.post("/api/get_dashboard_stats.php", data);

                if (response.status === 200 && response.data.error === false) {
                    setStats(response.data.data);
                }
            } catch (error) {
                console.error("Error fetching stats:", error);
            } finally {
                setStatsLoading(false);
            }
        };

        if (localStorage.getItem("adminlogintoken")) {
            fetchStats();
        }
    }, []);

    useEffect(() => {
        const data = {
            token : localStorage.getItem("adminlogintoken"),
            search : search,
            limitzero : true,
        }
        axios.post("/api/get_productlist.php", data)
            .then(response => {
                console.log(response.data);
                if (response.status === 200) {
                    if (response.data.error === false) {
                        setProductList(response.data.data);
                    } else {
                        localStorage.clear();
                    }
                }
            })
            .catch(error => {
                console.error(error);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [search]);

    const handleSort = useCallback((field) => {
        setSortConfig(prev => ({
            field,
            direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    }, []);

    const Redirect = () => {
        return (<Navigate replace to="/login"/>)
    }

    function toEnglishDigits(str) {
        // convert persian digits [۰۱۲۳۴۵۶۷۸۹]
        var e = '۰'.charCodeAt(0);
        str = str.replace(/[۰-۹]/g, function(t) {
            return t.charCodeAt(0) - e;
        });

        // convert arabic indic digits [٠١٢٣٤٥٦٧٨٩]
        e = '٠'.charCodeAt(0);
        str = str.replace(/[٠-٩]/g, function(t) {
            return t.charCodeAt(0) - e;
        });
        return str;
    }

    function number_format(number){
        let pe=toEnglishDigits(number);
        pe = String(pe).replace(/,/g, '');
        let nf = new Intl.NumberFormat("en-US");
        if (nf.format(pe) !== "NaN")
            return (nf.format(pe));
    }

    // فرمت کردن اعداد با جداکننده هزارگان
    const formatNumber = (num) => {
        if (num === undefined || num === null) return '0';
        return num.toLocaleString('en-US');
    };

    const handleStockOptionChange = (optionKey) => {
        console.log("Selected option:", optionKey);
        // می‌توانید اینجا هر منطق دیگری که نیاز دارید اضافه کنید
    };

    return (
        <div>
            {localStorage.getItem("adminlogintoken") === "" ? <Redirect/> : ''}
            {localStorage.getItem("adminlogintoken") === null ? <Redirect/> : ''}
            {errorMsg !== '' ? <ErrorMsg message={errorMsg} openn={true} seterrormsg={setErrorMsg}/> : ''}
            {succsMsg !== '' ? <SuccsMsg message={succsMsg} openn={true} setsuccsmsg={setSuccsMsg}/> : ''}

            <Grid container spacing={2} sx={{marginTop: 0}}>
                <Grid item xs={12} md={10} sx={{backgroundColor: "#ffffff", minHeight: "100vh"}}>
                    <Appbar/>

                    <Box sx={{padding: 2}} dir={"rtl"}>
                        {/* باکس‌های آمار */}
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                            {/* تاریخ امروز */}
                            <Grid item xs={12} sm={6} md={3}>
                                <StatsBox color="#2196f3">
                                    <StatsIcon>
                                        <CalendarTodayIcon sx={{ fontSize: 40 }} />
                                    </StatsIcon>
                                    <StatsContent>
                                        <StatsNumber variant="h5">
                                            {statsLoading ? <CircularProgress size={20} color="inherit" /> : stats.today_display}
                                        </StatsNumber>
                                        <StatsLabel variant="body2">تاریخ امروز</StatsLabel>
                                    </StatsContent>
                                </StatsBox>
                            </Grid>

                            {/* تعداد کل کالاها */}
                            <Grid item xs={12} sm={6} md={3}>
                                <StatsBox color="#4caf50">
                                    <StatsIcon>
                                        <InventoryIcon sx={{ fontSize: 40 }} />
                                    </StatsIcon>
                                    <StatsContent>
                                        <StatsNumber variant="h5">
                                            {statsLoading ? <CircularProgress size={20} color="inherit" /> : formatNumber(stats.total_products)}
                                        </StatsNumber>
                                        <StatsLabel variant="body2">تعداد کل کالاها</StatsLabel>
                                    </StatsContent>
                                </StatsBox>
                            </Grid>

                            {/* باکس قابل تغییر موجودی - با چهار گزینه */}
                            <Grid item xs={12} sm={6} md={3}>
                                <ToggleStatsBox
                                    stats={stats}
                                    statsLoading={statsLoading}
                                    color="#ff9800"
                                    icon={WarehouseIcon}
                                    label="موجودی امروز (کیلوگرم)"
                                    value={stats.today_stock}
                                    options={stockOptions}
                                    onOptionChange={handleStockOptionChange}
                                />
                            </Grid>

                            {/* مجموع فروش امروز */}
                            <Grid item xs={12} sm={6} md={3}>
                                <StatsBox color="#f44336">
                                    <StatsIcon>
                                        <TrendingUpIcon sx={{ fontSize: 40 }} />
                                    </StatsIcon>
                                    <StatsContent>
                                        <StatsNumber variant="h5">
                                            {statsLoading ? <CircularProgress size={20} color="inherit" /> : formatNumber(stats.today_sales)}
                                        </StatsNumber>
                                        <StatsLabel variant="body2">فروش امروز (کیلوگرم)</StatsLabel>
                                    </StatsContent>
                                </StatsBox>
                            </Grid>
                        </Grid>

                        {/* فیلد جستجو */}
                        <TextField
                            color={"info"}
                            autoComplete="off"
                            fullWidth
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder={"جست و جو بر اساس نام کالا"}
                            size={"small"}
                            sx={{marginBottom: 2}}
                            variant="outlined"
                        />

                        {/* جدول محصولات */}
                        <TableContainer component={Paper} sx={{ maxHeight: 'calc(100vh - 300px)' }}>
                            <Table stickyHeader sx={{ minWidth: 250 }}>
                                <TableHead>
                                    <TableRow>
                                        <StyledTableCell align={"center"}>
                                            <TableSortLabel
                                                active={sortConfig.field === 'name'}
                                                direction={sortConfig.field === 'name' ? sortConfig.direction : 'asc'}
                                                onClick={() => handleSort('name')}
                                                sx={{
                                                    color: 'white !important',
                                                    '& .MuiTableSortLabel-icon': {
                                                        color: 'white !important'
                                                    }
                                                }}
                                            >
                                                {"نام کالا"}
                                            </TableSortLabel>
                                        </StyledTableCell>
                                        <StyledTableCell align={"center"}>
                                            <TableSortLabel
                                                active={sortConfig.field === 'stock'}
                                                direction={sortConfig.field === 'stock' ? sortConfig.direction : 'asc'}
                                                onClick={() => handleSort('stock')}
                                                sx={{
                                                    color: 'white !important',
                                                    '& .MuiTableSortLabel-icon': {
                                                        color: 'white !important'
                                                    }
                                                }}
                                            >
                                                {"موجودی (کیلوگرم)"}
                                            </TableSortLabel>
                                        </StyledTableCell>
                                        <StyledTableCell align={"center"}>
                                            <TableSortLabel
                                                active={sortConfig.field === 'stockinway'}
                                                direction={sortConfig.field === 'stockinway' ? sortConfig.direction : 'asc'}
                                                onClick={() => handleSort('stockinway')}
                                                sx={{
                                                    color: 'white !important',
                                                    '& .MuiTableSortLabel-icon': {
                                                        color: 'white !important'
                                                    }
                                                }}
                                            >
                                                {"موجودی در راه (کیلوگرم)"}
                                            </TableSortLabel>
                                        </StyledTableCell>
                                        <StyledTableCell align={"center"}>
                                            <TableSortLabel
                                                active={sortConfig.field === 'todaysalle'}
                                                direction={sortConfig.field === 'todaysalle' ? sortConfig.direction : 'asc'}
                                                onClick={() => handleSort('todaysalle')}
                                                sx={{
                                                    color: 'white !important',
                                                    '& .MuiTableSortLabel-icon': {
                                                        color: 'white !important'
                                                    }
                                                }}
                                            >
                                                {"فروش امروز"}
                                            </TableSortLabel>
                                        </StyledTableCell>
                                        <StyledTableCell align={"center"}>
                                            <TableSortLabel
                                                active={sortConfig.field === 'remained'}
                                                direction={sortConfig.field === 'remained' ? sortConfig.direction : 'asc'}
                                                onClick={() => handleSort('remained')}
                                                sx={{
                                                    color: 'white !important',
                                                    '& .MuiTableSortLabel-icon': {
                                                        color: 'white !important'
                                                    }
                                                }}
                                            >
                                                {"مانده"}
                                            </TableSortLabel>
                                        </StyledTableCell>
                                    </TableRow>
                                </TableHead>

                                {sortedProductList.length > 0 ?
                                    <TableBody>
                                        {sortedProductList.map(list => (
                                            <StyledTableRow key={list.id}
                                                            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                                <StyledTableCell align={"center"}>
                                                    {list.name}
                                                </StyledTableCell>
                                                <StyledTableCell
                                                    sx={{backgroundColor: list.stock < 0 ? "#f8a5a5" : '', direction:"ltr"}}
                                                    component="th"
                                                    scope="row"
                                                    align={"center"}>
                                                    {list.stock?.toLocaleString()}
                                                </StyledTableCell>
                                                <StyledTableCell
                                                    sx={{backgroundColor: list.stockinway < 0 ? "#f8a5a5" : '', direction:"ltr"}}
                                                    component="th" scope="row"
                                                    align={"center"}>
                                                    {list.stockinway?.toLocaleString()}
                                                </StyledTableCell>
                                                <StyledTableCell component="th" scope="row"
                                                                 align={"center"}>
                                                    {list.todaysalle?.toLocaleString()}
                                                </StyledTableCell>
                                                <StyledTableCell
                                                    sx={{backgroundColor: (list.remained) < 0 ? "#f8a5a5" : '', direction:"ltr"}}
                                                    component="th" scope="row"
                                                    align={"center"}>
                                                    {list.remained?.toLocaleString()}
                                                </StyledTableCell>
                                            </StyledTableRow>
                                        ))}
                                    </TableBody>

                                    :
                                    !loading && (
                                        <TableBody>
                                            <TableRow>
                                                <StyledTableCell colSpan={5} align="center">
                                                    {search ? "موردی یافت نشد" : "هیچ داده‌ای موجود نیست"}
                                                </StyledTableCell>
                                            </TableRow>
                                        </TableBody>
                                    )
                                }
                            </Table>
                        </TableContainer>
                    </Box>
                </Grid>

                <Grid item xs={0} md={2} sx={{
                    backgroundColor: '#2b2b2b',
                    minHeight: "100vh",
                    display: {xs: "none", md: "block"}
                }}>
                    <MenuList/>
                </Grid>
            </Grid>

            <Dialog open={loading} scroll="paper">
                <CircularProgress sx={{margin: 2}}/>
            </Dialog>
        </div>
    )
}

export default Dashboard;